import ky from "ky";
import type {
  AcceptInviteResponse,
  AdditionalIncomeInput,
  AdditionalIncomesResponse,
  Address,
  AddressesResponse,
  AddressInput,
  Appointment,
  AppointmentInput,
  AppointmentsResponse,
  AuthResponse,
  BudgetInput,
  Document,
  DocumentsResponse,
  DocumentType,
  DocumentUpdateInput,
  Employment,
  EmploymentInput,
  EmploymentsResponse,
  FormOptions,
  IdentificationCard,
  IdentificationCardsResponse,
  InviteResponse,
  Location,
  LocationsResponse,
  Organization,
  PlaidLinkRecord,
  PlaidLinksResponse,
  Profile,
  ProfileUpdateInput,
  QuestionSet,
  Reference,
  ReferenceInput,
  ReferencesResponse,
  Rep,
  SignUpResponse,
  SubmitAnswerInput,
  SubmittedAnswer,
  TasksResponse,
  TaskVehiclesResponse,
  VehicleBudgetRange,
  VehicleOptions,
  VehiclePreferences,
  VehiclePreferencesInput,
} from "@/lib/api.types";
import type { ConsentAcceptance } from "@/lib/consent";
// The session module reaches this one through Task progress, so its functions may be called
// only inside the ky hooks and method bodies below, never while this module evaluates.
import {
  authorizeRequest,
  endSessionIfRejected,
  SIGN_IN_REQUEST_CONTEXT,
} from "@/lib/session";

function getApiPrefix(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const hostname = window.location.hostname;

  if (hostname === "demo.approveowl.com") {
    return "https://sandbox.approveowl.com";
  }

  if (hostname.endsWith(".approvehub.com")) {
    return "https://dealers.approveowl.com";
  }

  return "https://api.approveowl.com";
}

// Fixed for the page's lifetime, so callers never pass it. Read at call time, not on import.
function organizationDomain(): string {
  return import.meta.env.VITE_ORG_DOMAIN || window.location.hostname;
}

const api = ky.create({
  prefix: getApiPrefix(),
  timeout: 60_000,
  // TanStack Query owns retries; ky retrying underneath would multiply them.
  retry: 0,
  hooks: {
    beforeRequest: [
      ({ request, options }) => authorizeRequest(request, options.context),
    ],
    afterResponse: [
      ({ request, response }) => endSessionIfRejected(request, response),
    ],
  },
});

export const consumerApi = {
  getOrganization(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/organization", {
        searchParams: { domain: organizationDomain() },
        signal,
      })
      .json<Organization>();
  },

  sendCode(phoneNumber: string, channel = "sms") {
    return api
      .post("api/v2/consumer/session/send_code", {
        json: {
          domain: organizationDomain(),
          phone_number: phoneNumber,
          channel,
        },
      })
      .json<{ success: boolean; message: string }>();
  },

  signUp({
    user_identifier,
    sales_zone_identifier,
    consents,
    ...signUp
  }: {
    full_name: string;
    email_address: string;
    phone_number: string;
    user_identifier?: string;
    sales_zone_identifier?: string;
    consents?: ConsentAcceptance[];
  }) {
    return api
      .post("api/v2/consumer/sign_up", {
        json: {
          domain: organizationDomain(),
          sign_up: signUp,
          // Rails reads the referral identifiers and consents from the top level.
          user_identifier,
          sales_zone_identifier,
          consents,
        },
      })
      .json<SignUpResponse>();
  },

  // verifyCode, acceptInvite and exchangeImpersonationCode establish a session,
  // so they never carry one.
  verifyCode(
    phoneNumber: string,
    verificationCode: string,
    referral?: { user_identifier?: string; sales_zone_identifier?: string },
    consents?: ConsentAcceptance[],
  ) {
    return api
      .post("api/v2/consumer/session/verify_code", {
        context: SIGN_IN_REQUEST_CONTEXT,
        json: {
          domain: organizationDomain(),
          phone_number: phoneNumber,
          verification_code: verificationCode,
          ...referral,
          consents,
        },
      })
      .json<AuthResponse>();
  },

  getTasks(signal?: AbortSignal) {
    return api.get("api/v2/consumer/tasks", { signal }).json<TasksResponse>();
  },

  getFormOptions(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/form_options", { signal })
      .json<FormOptions>();
  },

  dismissTask(id: string, data: { reason: string; note?: string }) {
    return api.post(`api/v2/consumer/tasks/${id}/dismiss`, { json: data });
  },

  reissueTask(id: string) {
    return api
      .post(`api/v2/consumer/tasks/${id}/reissue`)
      .json<{ id: string }>();
  },

  respondToTask(id: string, choice: string) {
    return api.post(`api/v2/consumer/tasks/${id}/respond`, {
      json: { choice },
    });
  },

  getProfile(signal?: AbortSignal) {
    return api.get("api/v2/consumer/profile", { signal }).json<Profile>();
  },

  updateProfile({
    profile,
    consents,
  }: {
    profile: ProfileUpdateInput;
    consents?: ConsentAcceptance[];
  }) {
    return api
      .patch("api/v2/consumer/profile", { json: { profile, consents } })
      .json<Profile>();
  },

  getAddresses(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/addresses", { signal })
      .json<AddressesResponse>();
  },

  createAddress(data: AddressInput) {
    return api
      .post("api/v2/consumer/addresses", { json: { address: data } })
      .json<Address>();
  },

  getEmployments(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/employments", { signal })
      .json<EmploymentsResponse>();
  },

  createEmployment(data: EmploymentInput) {
    return api
      .post("api/v2/consumer/employments", { json: { employment: data } })
      .json<Employment>();
  },

  getAdditionalIncomes(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/additional_incomes", { signal })
      .json<AdditionalIncomesResponse>();
  },

  // Every row saves or none does. An empty list confirms the sources already on file.
  createAdditionalIncomes(data: AdditionalIncomeInput[]) {
    return api
      .post("api/v2/consumer/additional_incomes", {
        json: { additional_incomes: data },
      })
      .json<AdditionalIncomesResponse>();
  },

  deleteAdditionalIncome(id: string) {
    return api
      .delete(`api/v2/consumer/additional_incomes/${id}`)
      .json<{ success: boolean }>();
  },

  getReferences(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/references", { signal })
      .json<ReferencesResponse>();
  },

  createReference(data: ReferenceInput) {
    return api
      .post("api/v2/consumer/references", { json: { reference: data } })
      .json<Reference>();
  },

  getIdentificationCards(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/identification_cards", { signal })
      .json<IdentificationCardsResponse>();
  },

  createIdentificationCard(data: FormData) {
    return api
      .post("api/v2/consumer/identification_cards", { body: data })
      .json<IdentificationCard>();
  },

  getDocuments(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/documents", { signal })
      .json<DocumentsResponse>();
  },

  getDocumentTypes(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/documents/document_types", { signal })
      .json<{ document_types: DocumentType[] }>();
  },

  createDocument(data: FormData) {
    return api
      .post("api/v2/consumer/documents", { body: data })
      .json<Document>();
  },

  updateDocument(id: string, data: DocumentUpdateInput) {
    return api
      .patch(`api/v2/consumer/documents/${id}`, { json: { document: data } })
      .json<Document>();
  },

  deleteDocument(id: string) {
    return api
      .delete(`api/v2/consumer/documents/${id}`)
      .json<{ success: boolean }>();
  },

  getAppointments(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/appointments", { signal })
      .json<AppointmentsResponse>();
  },

  getLocations(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/appointments/locations", { signal })
      .json<LocationsResponse>();
  },

  getLocation(id: string, signal?: AbortSignal) {
    return api
      .get(`api/v2/consumer/locations/${id}`, {
        searchParams: { domain: organizationDomain() },
        signal,
      })
      .json<Location>();
  },

  getRep(id: string, signal?: AbortSignal) {
    return api
      .get(`api/v2/consumer/users/${id}`, {
        searchParams: { domain: organizationDomain() },
        signal,
      })
      .json<Rep>();
  },

  createAppointment(data: AppointmentInput) {
    return api
      .post("api/v2/consumer/appointments", { json: { appointment: data } })
      .json<Appointment>();
  },

  deleteAppointment(id: string) {
    return api
      .delete(`api/v2/consumer/appointments/${id}`)
      .json<{ success: boolean }>();
  },

  getPlaidLinks(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/plaid_links", { signal })
      .json<PlaidLinksResponse>();
  },

  // With a link id, the token opens Link in update mode to repair that bank.
  createPlaidLinkToken(plaidLinkId?: string) {
    return api
      .post(
        "api/v2/consumer/plaid_links/link_token",
        plaidLinkId ? { json: { plaid_link_id: plaidLinkId } } : undefined,
      )
      .json<{ link_token: string }>();
  },

  reconnectPlaidLink(id: string) {
    return api
      .patch(`api/v2/consumer/plaid_links/${id}`)
      .json<PlaidLinkRecord>();
  },

  createPlaidLink(data: {
    public_token: string;
    metadata: Record<string, unknown>;
  }) {
    return api
      .post("api/v2/consumer/plaid_links", { json: data })
      .json<PlaidLinkRecord>();
  },

  logPlaidLinkEvent(data: {
    name: string;
    timestamp: string;
    metadata: Record<string, unknown>;
    is_error: boolean;
  }) {
    return api.post("api/v2/consumer/plaid_links/log_event", { json: data });
  },

  getInvite(slug: string, signal?: AbortSignal) {
    return api
      .get(`api/v2/consumer/invites/${slug}`, { signal })
      .json<InviteResponse>();
  },

  acceptInvite(slug: string, consents?: ConsentAcceptance[]) {
    return api
      .post(`api/v2/consumer/invites/${slug}/accept`, {
        context: SIGN_IN_REQUEST_CONTEXT,
        json: { consents },
      })
      .json<AcceptInviteResponse>();
  },

  exchangeImpersonationCode(code: string) {
    return api
      .post("api/v2/consumer/impersonation/exchange", {
        json: { code },
        context: SIGN_IN_REQUEST_CONTEXT,
      })
      .json<AuthResponse>();
  },

  getQuestionSet(id: string, signal?: AbortSignal) {
    return api
      .get(`api/v2/consumer/question_sets/${id}`, { signal })
      .json<QuestionSet>();
  },

  submitAnswer(questionSetId: string, data: SubmitAnswerInput) {
    return api
      .post(`api/v2/consumer/question_sets/${questionSetId}/answers`, {
        json: { answer: data },
      })
      .json<SubmittedAnswer>();
  },

  submitQuestionSet(questionSetId: string, answers: SubmitAnswerInput[]) {
    return api
      .post(`api/v2/consumer/question_sets/${questionSetId}/submission`, {
        json: { answers },
      })
      .json<QuestionSet>();
  },

  getVehiclePreferences(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/vehicle_preferences", { signal })
      .json<VehiclePreferences>();
  },

  updateVehiclePreferences(data: VehiclePreferencesInput) {
    return api
      .put("api/v2/consumer/vehicle_preferences", {
        json: { vehicle_preferences: data },
      })
      .json<VehiclePreferences>();
  },

  getVehicleOptions(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/vehicle_options", { signal })
      .json<VehicleOptions>();
  },

  getVehicleBudgetRange(signal?: AbortSignal) {
    return api
      .get("api/v2/consumer/vehicle_budget_range", { signal })
      .json<VehicleBudgetRange>();
  },

  // The narrow write behind the budget sliders: touches only the three budget rows, so it can
  // safely fire on every slider release without taking the make/model/color rows with it.
  updateBudget(data: BudgetInput) {
    return api
      .patch("api/v2/consumer/vehicle_preferences/budget", {
        json: { budget: data },
      })
      .json<VehiclePreferences>();
  },

  getTaskVehicles(taskId: string, signal?: AbortSignal) {
    return api
      .get(`api/v2/consumer/tasks/${taskId}/vehicles`, { signal })
      .json<TaskVehiclesResponse>();
  },

  selectVehicle(taskId: string, vin: string) {
    return api.post(`api/v2/consumer/tasks/${taskId}/select_vehicle`, {
      json: { vin },
    });
  },

  deselectVehicle(taskId: string, vin: string) {
    return api.post(`api/v2/consumer/tasks/${taskId}/deselect_vehicle`, {
      json: { vin },
    });
  },

  submitTaskVehicles(taskId: string) {
    return api.post(`api/v2/consumer/tasks/${taskId}/submit_vehicles`);
  },
};
